import React, { useState, useRef, useEffect } from "react";
import { Box, IconButton, InputBase, Paper } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  disabled = false,
}) => {
  const [message, setMessage] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      const scrollHeight = inputRef.current.scrollHeight;
      // Max 4 lines (approximately 100px)
      const maxHeight = 100;
      inputRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [message]);

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage("");
      // Reset height after sending
      if (inputRef.current) {
        inputRef.current.style.height = "auto";
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAttach = () => {
    console.log("Attach file clicked");
    // Future: implement file attachment
  };

  return (
    <Paper
      elevation={0}
      sx={{
        px: 2,
        py: 1.5,
        borderTop: "1px solid #E0E0E0",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
        }}
      >
        {/* Attach button */}
        <IconButton
          size="medium"
          onClick={handleAttach}
          disabled={disabled}
          sx={{
            color: "text.secondary",
            "&:hover": { backgroundColor: "rgba(23, 139, 142, 0.08)" },
          }}
        >
          <AttachFileIcon />
        </IconButton>

        {/* Message input */}
        <InputBase
          inputRef={inputRef}
          multiline
          maxRows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type a message..."
          disabled={disabled}
          sx={{
            flex: 1,
            px: 2,
            py: 1,
            backgroundColor: "#F5F5F5",
            borderRadius: 3,
            fontSize: "0.95rem",
            "& .MuiInputBase-input": {
              padding: 0,
              maxHeight: 100,
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,.2)",
                borderRadius: "3px",
              },
            },
          }}
        />

        {/* Send button */}
        <IconButton
          size="medium"
          onClick={handleSend}
          disabled={!message.trim() || disabled}
          sx={{
            color: "white",
            backgroundColor: message.trim() && !disabled ? "#178B8E" : "#BDBDBD",
            "&:hover": {
              backgroundColor: message.trim() && !disabled ? "#0F6164" : "#BDBDBD",
            },
            "&.Mui-disabled": {
              backgroundColor: "#BDBDBD",
              color: "white",
            },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>

      {/* Helper text */}
      <Box sx={{ mt: 0.5, px: 6 }}>
        <Box
          component="span"
          sx={{
            fontSize: "0.7rem",
            color: "text.secondary",
          }}
        >
          Press Enter to send, Shift+Enter for new line
        </Box>
      </Box>
    </Paper>
  );
};

export default MessageInput;
